<template>
  <div class="category-order-selector">
    <!-- Available Categories -->
    <div class="categories-section">
      <label class="section-label">Categorías Disponibles</label>
      <div class="available-categories">
        <div
          v-for="category in availableCategories"
          :key="'available-' + category.id"
          class="category-item available"
          @click="addCategory(category)"
        >
          <span class="category-name">{{ category.label }}</span>
          <i class="fas fa-plus-circle add-icon"></i>
        </div>
        <div v-if="availableCategories.length === 0" class="empty-state">
          Todas las categorías han sido seleccionadas
        </div>
      </div>
    </div>

    <!-- Selected Categories (Orderable) -->
    <div class="categories-section">
      <label class="section-label">
        Categorías Seleccionadas ({{ selectedCategories.length }})
        <small class="text-muted">Arrastra para ordenar</small>
      </label>
      <div class="selected-categories">
        <transition-group name="list" tag="div">
          <div
            v-for="(category, index) in selectedCategories"
            :key="'selected-' + category.id"
            class="category-item selected"
            draggable="true"
            @dragstart="onDragStart(index)"
            @dragover.prevent="onDragOver(index)"
            @dragend="onDragEnd"
            @drop="onDrop(index)"
            :class="{ 'dragging': draggedIndex === index }"
          >
            <div class="drag-handle">
              <i class="fas fa-grip-vertical"></i>
            </div>
            <div class="category-content">
              <span class="category-number">{{ index + 1 }}.</span>
              <span class="category-name">{{ category.label }}</span>
            </div>
            <button
              type="button"
              class="remove-btn"
              @click="removeCategory(index)"
              title="Eliminar categoría"
            >
              <i class="fas fa-times-circle"></i>
            </button>
          </div>
        </transition-group>
        <div v-if="selectedCategories.length === 0" class="empty-state">
          <i class="fas fa-arrow-left"></i>
          Selecciona categorías de la lista
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CategoryOrderSelector",
  props: {
    categories: {
      type: Array,
      required: true,
      // Expected format: [{ id: 'uuid', label: 'Category Name' }]
    },
    value: {
      type: Array,
      default: () => [],
      // Array of category IDs in order
    },
  },
  data() {
    return {
      draggedIndex: null,
      dragOverIndex: null,
    };
  },
  computed: {
    selectedCategories() {
      // Map selected IDs to full category objects, maintaining order
      return this.value
        .map((id) => this.categories.find((cat) => cat.id === id))
        .filter(Boolean); // Remove any nulls if ID not found
    },
    availableCategories() {
      // Categories not yet selected
      return this.categories.filter(
        (cat) => !this.value.includes(cat.id)
      );
    },
  },
  methods: {
    addCategory(category) {
      const newValue = [...this.value, category.id];
      this.$emit("input", newValue);
    },
    removeCategory(index) {
      const newValue = [...this.value];
      newValue.splice(index, 1);
      this.$emit("input", newValue);
    },
    onDragStart(index) {
      this.draggedIndex = index;
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDrop(dropIndex) {
      if (this.draggedIndex === null) return;

      const newValue = [...this.value];
      const draggedItem = newValue[this.draggedIndex];

      // Remove from old position
      newValue.splice(this.draggedIndex, 1);
      // Insert at new position
      newValue.splice(dropIndex, 0, draggedItem);

      this.$emit("input", newValue);
    },
    onDragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.category-order-selector {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.categories-section {
  flex: 1;
  min-width: 280px;
}

.section-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: #495057;
  font-size: 0.95rem;

  small {
    font-weight: normal;
    margin-left: 0.5rem;
    font-size: 0.85rem;
  }
}

.available-categories,
.selected-categories {
  border: 2px solid #dee2e6;
  border-radius: 0.5rem;
  padding: 1rem;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #f8f9fa;
}

.category-item {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  background-color: white;
  border: 1px solid #dee2e6;

  &:last-child {
    margin-bottom: 0;
  }

  .category-name {
    flex: 1;
    font-size: 0.9rem;
  }
}

.category-item.available {
  cursor: pointer;

  &:hover {
    background-color: #e7f5ff;
    border-color: #339af0;
    transform: translateX(4px);

    .add-icon {
      color: #339af0;
    }
  }

  .add-icon {
    color: #28a745;
    font-size: 1.1rem;
    transition: color 0.2s;
  }
}

.category-item.selected {
  cursor: move;
  gap: 0.5rem;

  &:hover {
    background-color: #fff4e6;
    border-color: #fd7e14;
  }

  &.dragging {
    opacity: 0.5;
    transform: rotate(2deg);
  }

  .drag-handle {
    color: #6c757d;
    cursor: grab;
    font-size: 1rem;

    &:active {
      cursor: grabbing;
    }
  }

  .category-content {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .category-number {
    font-weight: 600;
    color: #6c757d;
    min-width: 24px;
  }

  .remove-btn {
    background: none;
    border: none;
    color: #dc3545;
    cursor: pointer;
    padding: 0.25rem;
    font-size: 1.1rem;
    transition: all 0.2s;

    &:hover {
      color: #c82333;
      transform: scale(1.2);
    }

    &:focus {
      outline: none;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #6c757d;
  font-style: italic;
  text-align: center;
  min-height: 100px;

  i {
    margin-right: 0.5rem;
  }
}

// Transition animations
.list-move {
  transition: transform 0.3s ease;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter,
.list-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

// Custom scrollbar
.available-categories::-webkit-scrollbar,
.selected-categories::-webkit-scrollbar {
  width: 8px;
}

.available-categories::-webkit-scrollbar-track,
.selected-categories::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.available-categories::-webkit-scrollbar-thumb,
.selected-categories::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;

  &:hover {
    background: #555;
  }
}
</style>
